import { z } from 'zod'
import { errorParentIsRemoved, Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import { graphql } from '@/gql'
import type {
  Coassign_Custom_Field_Choice_Insert_Input,
  Coassign_Custom_Field_Choice_Set_Input
} from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { formatISO } from 'date-fns'
import { pick } from '@/lib/utils/functional'
import { withStage } from '@/lib/layered'
import type { Kysely } from 'kysely'
import type { DB } from '@/lib/kysely-d'
import { type Reactive } from 'vue'
import { isExist } from '@/lib/relation-graph'

const TABLE_NAME = 'custom_field_choice' as const

const isUniqueKey = async ({ id, key }: { id: string; key: string }): Promise<Boolean> => {
  const isExist = await withStage(async (db: Kysely<DB>) => {
    return await db
      .selectFrom(TABLE_NAME)
      .select('key')
      .where('key', '=', key)
      .where('id', '!=', id)
      .execute()
  })
  if (isExist.length > 0) return false
  return true
}

// 先頭はA-zのみ、それに続くのはA-z, 0-9, "-", "_"の文字
const regex = /^[A-Za-z][A-Za-z0-9\-_]*$/

export const customFieldChoiceSchema = z
  .object({
    id: z.string().max(21),
    name: z.string().trim().min(2).max(16),
    key: z.string().min(2).max(32).regex(regex),
    order: z.number().default(0),
    custom_field_id: z.string().max(21).refine(isExist('custom_field'), errorParentIsRemoved),
    visible: z.boolean()
  })
  .refine(isUniqueKey, { message: 'そのキー名はすでに使われています', path: ['key'] })

export type CustomFieldChoice = z.input<typeof customFieldChoiceSchema>

export class CustomFieldChoiceMutator extends Mutator<typeof customFieldChoiceSchema> {
  static readonly stageableClassName = 'CustomFieldChoiceMutator'
  schema = customFieldChoiceSchema
  table = TABLE_NAME

  static async load(
    id: string,
    custom_field_id: string
  ): Promise<Reactive<CustomFieldChoiceMutator>> {
    const self = this.createInstance()
    // FIXME: need use watch query
    const base = await DataBase.selectFrom(self.table)
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()

    self.context = { id, custom_field_id }
    // 更新の場合、型を、DB用から一般化
    if (base) {
      self.base = {
        ...base,
        ...{ visible: Boolean(base.visible) }
      }
    }
    return self
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const sqliteParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => [
        key,
        typeof value === 'boolean' ? Number(value) : value
      ])
    )
    return [DataBase.updateTable(this.table).set(sqliteParams).where('id', '=', id).compile()]
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const tenant_id = useUserStore().userInfo!.tenantId
    const params = { ...(await this.parsed)!, ...{ tenant_id } }
    const sqliteParams = {
      ...params,
      ...{ visible: Number(params.visible) }
    }
    return [DataBase.insertInto(this.table).values(sqliteParams).compile()]
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    const id = this.context.id!
    return [DataBase.deleteFrom(this.table).where('id', '=', id).compile()]
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const params = (await this.parsed)!
    const variables: Coassign_Custom_Field_Choice_Insert_Input = { ...params, ...{ commit_id } }
    return [{ mutation: insertCustomFieldChoiceMutationDocument, variables: { input: variables } }]
  }

  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const variables: Coassign_Custom_Field_Choice_Set_Input = {
      commit_id: commitId,
      deleted_at: formatISO(new Date())
    }
    return [
      { mutation: updateCustomFieldChoiceMutationDocument, variables: { id: id, input: variables } }
    ]
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const variables: Coassign_Custom_Field_Choice_Set_Input = { ...params, ...{ commit_id } }
    return [
      { mutation: updateCustomFieldChoiceMutationDocument, variables: { id: id, input: variables } }
    ]
  }
}

const insertCustomFieldChoiceMutationDocument = graphql(/* GraphQL */ `
  mutation insertCustomFieldChoice($input: coassign_custom_field_choice_insert_input!) {
    insert_coassign_custom_field_choice_one(object: $input) {
      created_at
    }
  }
`)

const updateCustomFieldChoiceMutationDocument = graphql(/* GraphQL */ `
  mutation updateCustomFieldChoice($id: String!, $input: coassign_custom_field_choice_set_input!) {
    update_coassign_custom_field_choice_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)
